import { createContext, useCallback, useRef, useState } from 'react'
import { v4 } from 'uuid'
import { SOCKET_URL } from '../env/env'
import { CHALLENGE_TYPES } from '../utils/constant'
import {
  createRunCodeTimer,
  getJWTToken,
  needEventTimeout
} from '../utils/utils'

export const SocketContext = createContext({
  socket: null,
  initSocket: () => null,
  closeSocket: () => null,
  isSocketConnected: false,
  IntialiseMCQ: () => null,
  IntialiseEditor: () => null,
  sendEditorRunCode: () => null,
  sendEditorFileOpen: () => null,
  sendEditorFileSave: () => null,
  SendSocketHeartBeat: () => null,
  sendSocketReconnect: () => null,
  sendEditorFileDelete: () => null,
  sendEditorFileChange: () => null,
  getMediaServerEndPoint: () => null,
  sendSubmitContestEditor: () => null,
  sendCloseOtherConnection: () => null,
  sendCandidateDisqualify: () => null,
  sendInstallDependency: () => null,
  sendEditorCodeForIntellisense: () => null,
  handleSocketAuthorization: () => null,
  sendDBInit: () => null,
  sendDBLoad: () => null,
  sendRunSQLScript: () => null,
  sendSQLChange: () => null,
  sendRunAppSQL: () => null,
  sendResetAppDatabase: () => null
})

export const SocketProvider = ({ children }) => {
  // Refs
  const socket = useRef(null)
  const reconnectIntervalId = useRef(null)
  const checkConnectionIdRef = useRef(null)
  const checkConnectionTimeoutRef = useRef(null)
  const isSocketClosedIntentionally = useRef(false)

  // State
  const [isSocketConnected, setIsSocketConnected] = useState(false)
  const [socketAuthenticated, setSocketAuthenticated] = useState(false)

  // Handlers

  const sendMessage = useCallback((method, parameters = {}) => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      const id = v4()

      const needTimer = needEventTimeout(method)

      if (needTimer) {
        createRunCodeTimer(id, needTimer?.key, needTimer?.message)
      } else if (method === 'check-connection') {
        checkConnectionIdRef.current = id
      }

      socket.current.send(JSON.stringify({ id, method, parameters }))
      return true
    }
    return false
  }, [])

  const clearCheckConnectionTimeoutRef = useCallback(() => {
    if (checkConnectionTimeoutRef.current) {
      clearTimeout(checkConnectionTimeoutRef.current)
      checkConnectionTimeoutRef.current = null
    }
  }, [])

  const clearReconnectIntervalId = useCallback(() => {
    if (reconnectIntervalId.current) {
      clearInterval(reconnectIntervalId.current)
      reconnectIntervalId.current = null
    }
  }, [])

  const closeSocket = useCallback(
    (isClosingIntionally = true) => {
      if (socket.current) {
        clearReconnectIntervalId()
        isSocketClosedIntentionally.current = isClosingIntionally
        socket.current.close(1000, 'Normal Closure')
        setIsSocketConnected(false)
      }
    },
    [clearReconnectIntervalId]
  )

  const initSocket = useCallback(
    (socketUrl) => {
      if (socket.current && socket.current.readyState === WebSocket.CLOSING) {
        socket.current = null
        initSocket(socketUrl)
      } else if (
        !socket.current ||
        socket.current.readyState === WebSocket.CLOSED
      ) {
        const newSocket = new WebSocket(
          socketUrl || `${SOCKET_URL}ws/challenge/editor`
        )

        const handleSocketReconnect = () => {
          if (
            !isSocketClosedIntentionally.current &&
            !reconnectIntervalId.current
          ) {
            reconnectIntervalId.current = setInterval(() => {
              initSocket(socketUrl)
            }, 2000)
          }
        }

        const handleSendConnectionMessage = () => {
          sendMessage('check-connection')
          checkConnectionTimeoutRef.current = setTimeout(() => {
            if (isSocketClosedIntentionally.current) {
              clearCheckConnectionTimeoutRef()
            } else {
              closeSocket(false)
              handleSocketReconnect()
            }
          }, 5000)
        }

        const handleOpen = () => {
          setIsSocketConnected(true)
          clearReconnectIntervalId()
          clearCheckConnectionTimeoutRef()

          isSocketClosedIntentionally.current = false
          handleSendConnectionMessage()
        }

        const handleSocketMessage = (event) => {
          const data = JSON.parse(event?.data || null)
          if (data?.method === 'check-connection-response') {
            if (data?.id === checkConnectionIdRef.current) {
              clearCheckConnectionTimeoutRef()
              checkConnectionIdRef.current = null

              if (!isSocketClosedIntentionally.current) {
                setTimeout(() => {
                  handleSendConnectionMessage()
                }, 1000)
              }
            } else {
              if (!isSocketClosedIntentionally.current) {
                closeSocket(false)
                handleSocketReconnect()
              }
            }
          }
        }

        const handleClose = () => {
          setIsSocketConnected(false)
          if (!isSocketClosedIntentionally.current) {
            handleSocketReconnect()
          }
        }

        // Remove existing event listeners if they exist
        if (socket.current) {
          socket.current.removeEventListener('open', handleOpen)
          socket.current.removeEventListener('close', handleClose)
          // socket.current.removeEventListener('error', handleError)
          socket.current.removeEventListener('message', handleSocketMessage)
        }

        newSocket.addEventListener('open', handleOpen)
        newSocket.addEventListener('close', handleClose)
        // newSocket.addEventListener('error', handleError)
        newSocket.addEventListener('message', handleSocketMessage)

        socket.current = newSocket
      }
    },
    [
      closeSocket,
      sendMessage,
      clearReconnectIntervalId,
      clearCheckConnectionTimeoutRef
    ]
  )

  const SendSocketHeartBeat = useCallback(() => {
    sendMessage('challenge-editor/heartbeat')
  }, [sendMessage])

  const handleSocketAuthorization = useCallback(() => {
    sendMessage('challenge-editor/authorization', { token: getJWTToken() })
  }, [sendMessage])

  const IntialiseEditor = useCallback(
    (id, attemptId, contestId) => {
      sendMessage('challenge-editor/load', {
        programmingChallengeId: id,
        ...(contestId ? { contestId } : {}),
        ...(attemptId ? { attemptId } : {})
      })
    },
    [sendMessage]
  )

  const IntialiseMCQ = useCallback(
    (challengeId, attemptId) => {
      sendMessage('challenge-mcq/load', {
        challengeId,
        attemptId
      })
    },
    [sendMessage]
  )

  const sendEditorFileOpen = useCallback(
    (filePath) => {
      sendMessage('file/open', { path: filePath })
    },
    [sendMessage]
  )

  const sendEditorFileChange = useCallback(
    (file) => {
      sendMessage('file/change', file)
    },
    [sendMessage]
  )

  const sendEditorFileDelete = useCallback(
    (files) => {
      files.forEach((file) => {
        sendMessage('file/delete', { path: file?.path })
      })
    },
    [sendMessage]
  )

  const sendEditorFileSave = useCallback(
    (files, dependencies, challengeType) => {
      return sendMessage(
        challengeType === CHALLENGE_TYPES.NOTEBOOK
          ? 'challenge-editor/notebook-save'
          : 'challenge-editor/save',
        {
          ...(challengeType === CHALLENGE_TYPES.NOTEBOOK
            ? { files }
            : {
                files: files.map((file) => ({
                  candidate: file?.candidate,
                  content: file?.content,
                  path: file?.path
                })),
                dependencies
              })
        }
      )
    },
    [sendMessage]
  )

  const sendSocketReconnect = useCallback(
    (programmingChallengeId, files, attemptId, contestId) => {
      sendMessage('challenge-editor/reconnect', {
        ...(contestId ? { contestId } : {}),
        ...(attemptId ? { attemptId } : {}),
        programmingChallengeId,
        files: files.map((file) => ({
          candidate: file?.candidate,
          content: file?.content,
          path: file?.path
        }))
      })
    },
    [sendMessage]
  )

  const sendEditorRunCode = useCallback(
    (files, dbtCommand) => {
      return sendMessage('challenge-editor/run', {
        files: files.map((file) => ({
          content: file?.content,
          path: file?.path
        })),
        ...(dbtCommand?.trim() ? { dbtCommand } : {})
      })
    },
    [sendMessage]
  )

  const sendCandidateDisqualify = useCallback(
    (candidateId, attemptId) => {
      sendMessage('contest/disqualify-candidate-attempt', {
        candidateId,
        attemptId
      })
    },
    [sendMessage]
  )

  const sendCandidateSubmitAttempt = useCallback(
    (candidateId, attemptId) => {
      sendMessage('challenge-editor/supervisor-submit', {
        candidateId,
        attemptId
      })
    },
    [sendMessage]
  )

  const sendCloseOtherConnection = useCallback(() => {
    sendMessage('challenge-editor/close-other-connections')
  }, [sendMessage])

  const sendSubmitContestEditor = useCallback(() => {
    sendMessage('challenge-editor/submit')
  }, [sendMessage])

  const sendEditorCodeForIntellisense = useCallback(
    (params) => {
      sendMessage('file/completion', params)
    },
    [sendMessage]
  )

  const sendInstallDependency = useCallback(
    (dependencies) => {
      sendMessage('language-server/install-dependencies', { dependencies })
    },
    [sendMessage]
  )

  const getMediaServerEndPoint = useCallback(
    (attemptId, stageId, excludeEndpoints = []) => {
      sendMessage('media-server/get-endpoint', {
        ...(attemptId ? { attemptId } : {}),
        ...(stageId ? { stageId } : {}),
        excludeEndpoints: [...excludeEndpoints]
      })
    },
    [sendMessage]
  )

  const sendDBLoad = useCallback(
    (
      databaseRuntime,
      databaseId,
      challengeId,
      attemptId,
      sql,
      courseId,
      topicPageId
    ) => {
      sendMessage('database/load', {
        ...(sql ? { sql } : {}),
        ...(courseId ? { courseId } : {}),
        ...(attemptId ? { attemptId } : {}),
        ...(databaseId ? { databaseId } : {}),
        ...(challengeId ? { challengeId } : {}),
        ...(topicPageId ? { topicPageId } : {}),
        ...(databaseRuntime ? { databaseRuntime } : {})
      })
    },
    [sendMessage]
  )

  const sendDBInit = useCallback(
    (sqlScript) => {
      sendMessage('database/init', {
        sqlScript
      })
    },
    [sendMessage]
  )

  const sendRunSQLScript = useCallback(
    (sqlScript, sql, candidateSelectedSQL) => {
      sendMessage('database/run-sql', {
        ...(sqlScript ? { sqlScript } : {}),
        ...(sql ? { sql } : {}),
        ...(candidateSelectedSQL ? { selectedSql: candidateSelectedSQL } : {})
      })
    },
    [sendMessage]
  )

  const sendSQLChange = useCallback(
    (sql) => {
      sendMessage('database/sql-change', {
        ...(sql ? { sql } : {})
      })
    },
    [sendMessage]
  )

  const sendRunAppSQL = useCallback(
    (sql) => {
      sendMessage('database/run-app-sql', {
        sql: sql || ''
      })
    },
    [sendMessage]
  )

  const sendResetAppDatabase = useCallback(() => {
    sendMessage('database/reset-app-database', {})
  }, [sendMessage])

  return (
    <SocketContext.Provider
      value={{
        socket: socket.current,
        initSocket,
        sendDBLoad,
        sendDBInit,
        closeSocket,
        IntialiseMCQ,
        sendRunAppSQL,
        sendSQLChange,
        IntialiseEditor,
        sendRunSQLScript,
        isSocketConnected,
        sendEditorRunCode,
        sendEditorFileOpen,
        sendEditorFileSave,
        SendSocketHeartBeat,
        socketAuthenticated,
        sendSocketReconnect,
        sendResetAppDatabase,
        sendEditorFileDelete,
        sendEditorFileChange,
        sendInstallDependency,
        setSocketAuthenticated,
        getMediaServerEndPoint,
        sendSubmitContestEditor,
        sendCandidateDisqualify,
        sendCloseOtherConnection,
        sendCandidateSubmitAttempt,
        sendEditorCodeForIntellisense,
        handleSocketAuthorization
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}
